import React from "react";

const EXPERT_TAGS = [
  "Mechanical Engineers",
  "Electrical Engineers",
  "Electronics Experts",
  "Product Designers",
  "CAD / CAE Professionals",
  "Manufacturing Experts",
  "Tool & Fixture Designers",
  "Material Specialists",
  "Patent / IP Professionals",
  "Industrial Designers",
  "Quality Experts",
  "Supply Chain Experts",
  "Industry Consultants",
  "Startup Mentors",
];

const FEATURES = [
  "Build professional profile",
  "Define consultation charges",
  "Receive requests",
  "Guide innovators",
];

function CheckIcon() {
  return (
    <svg
      width="19"
      height="19"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={{ flexShrink: 0 }}
    >
      <circle cx="12" cy="12" r="9.25" stroke="#7f47ec" strokeWidth="1.6" />
      <path
        d="M8 12.5L10.5 15L16 9"
        stroke="#7f47ec"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ArrowIcon({ color = "#0B1F3A" }) {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M5 12h14M13 6l6 6-6 6"
        stroke={color}
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function LightningIcon() {
  return (
    <svg
      width="72"
      height="72"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M13 2L4 14h6l-1 8 9-12h-6l1-8z"
        stroke="rgba(255,255,255,0.18)"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function Tag({ text }) {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        background: "#FFFFFF",
        border: "1px solid #E5EAF1",
        borderRadius: "999px",
        padding: "10px 20px",
        fontSize: "15px",
        fontWeight: 600,
        color: "#0B1F3A",
        whiteSpace: "nowrap",
      }}
    >
      {text}
    </span>
  );
}

function FeatureBox({ text }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        background: "#FFFFFF",
        border: "1px solid #E5EAF1",
        borderRadius: "14px",
        padding: "18px 22px",
      }}
    >
      <CheckIcon />
      <span
        style={{
          fontSize: "16px",
          fontWeight: 600,
          color: "#0B1F3A",
        }}
      >
        {text}
      </span>
    </div>
  );
}

export default function ExpertsSection() {
  return (
    <section
      style={{
        background:
          "linear-gradient(135deg, #EAF3FC 0%, #F3F8FD 45%, #E9F5FB 100%)",
        padding: "72px 24px",
        fontFamily:
          "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      }}
    >
      <div
        className="exp-grid"
        style={{
          maxWidth: "1200px",
          margin: "0 auto",
          display: "grid",
          gridTemplateColumns: "1fr 1.05fr",
          gap: "60px",
          alignItems: "start",
        }}
      >
        {/* Left column */}
        <div>
          <span
            style={{
              display: "inline-block",
              fontSize: "13px",
              fontWeight: 700,
              letterSpacing: "1.5px",
              color: "#7f47ec",
              marginBottom: "18px",
              textTransform: "uppercase",
            }}
          >
            For Experts
          </span>

          <h2
            style={{
              fontSize: "46px",
              fontWeight: 800,
              color: "#0B1F3A",
              margin: "0 0 26px 0",
              lineHeight: 1.18,
            }}
          >
            Your Knowledge Can Create the{" "}
            <span style={{ color: "#7f47ec" }}>Next Big Product.</span>
          </h2>

          <p
            style={{
              fontSize: "17px",
              color: "#5B6779",
              lineHeight: 1.65,
              margin: "0 0 32px 0",
              maxWidth: "480px",
            }}
          >
            Experts can build professional profiles and offer consultation
            services to innovators — guiding, improving designs, and
            recommending processes and materials.
          </p>

          <div
            style={{
              position: "relative",
              background: "#0B1F3A",
              borderRadius: "18px",
              padding: "32px 34px",
              overflow: "hidden",
              boxShadow: "0 16px 30px -10px rgba(11,31,58,0.4)",
            }}
          >
            <div
              style={{
                position: "absolute",
                right: "18px",
                top: "50%",
                transform: "translateY(-50%)",
              }}
            >
              <LightningIcon />
            </div>
            <p
              style={{
                position: "relative",
                margin: 0,
                fontSize: "21px",
                fontWeight: 700,
                color: "#FFFFFF",
                lineHeight: 1.4,
                maxWidth: "460px",
              }}
            >
              One hour of your expertise can save an innovator{" "}
              <span style={{ color: "#7f47ec" }}>months of mistakes.</span>
            </p>
          </div>
        </div>

        {/* Right column */}
        <div>
          <h3
            style={{
              fontSize: "13px",
              fontWeight: 700,
              letterSpacing: "1.2px",
              color: "#0B1F3A",
              textTransform: "uppercase",
              margin: "0 0 18px 0",
            }}
          >
            Experts Include
          </h3>

          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: "12px",
              marginBottom: "30px",
            }}
          >
            {EXPERT_TAGS.map((tag) => (
              <Tag key={tag} text={tag} />
            ))}
          </div>

          <div
            className="exp-features"
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: "14px",
              marginBottom: "26px",
            }}
          >
            {FEATURES.map((f) => (
              <FeatureBox key={f} text={f} />
            ))}
          </div>

          <button
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "10px",
              background: "#7f47ec",
              color: "white",
              border: "none",
              borderRadius: "999px",
              padding: "16px 28px",
              fontSize: "15.5px",
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            Join as an Expert
            <ArrowIcon color="white" />
          </button>
        </div>
      </div>

      <style>{`
        @media (max-width: 960px) {
          .exp-grid {
            grid-template-columns: 1fr !important;
          }
        }
        @media (max-width: 560px) {
          .exp-features {
            grid-template-columns: 1fr !important;
          }
          h2 {
            font-size: 34px !important;
          }
        }
      `}</style>
    </section>
  );
}